// src/admin/components/OrganizationsList.tsx
import { Organization } from '@/types/organization';
import { Delete, Edit, Person, Settings } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

import { Restore } from '@mui/icons-material';

interface OrganizationsListProps {
  organizations: Organization[];
  selectedOrgId: string;
  onSelectOrganization: (orgId: string) => void;
  onDeleteOrganization: (org: Organization) => void;
  onEditOrganization: (org: Organization) => void;
  onManageUsers: (org: Organization) => void;
  onSettings: (org: Organization) => void;
  onRestoreOrganization: (org: Organization) => void;
}

export function OrganizationsList({
  organizations,
  selectedOrgId,
  onSelectOrganization,
  onDeleteOrganization,
  onEditOrganization,
  onManageUsers,
  onSettings,
  onRestoreOrganization,
}: OrganizationsListProps) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align={'center'}>Users</TableCell>
            <TableCell align={'center'}>Departments</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align={'right'}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {organizations.length === 0 ? (
            <TableRow key={'no-orgs'}>
              <TableCell colSpan={6} align={'center'}>
                <Typography variant={'body1'} color={'text.secondary'}>
                  No organizations found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            organizations.map((org) => (
              <TableRow
                key={org.id}
                selected={selectedOrgId === org.id}
                onClick={() => onSelectOrganization(org.id)}
                sx={{
                  cursor: 'pointer',
                  bgcolor:
                    selectedOrgId === org.id ? 'action.selected' : undefined,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <TableCell>{org.name}</TableCell>
                <TableCell>{org.description}</TableCell>
                <TableCell align={'center'}>
                  <Chip
                    label={org.userCount}
                    size={'small'}
                    color={'primary'}
                  />
                </TableCell>
                <TableCell align={'center'}>
                  <Chip
                    label={org.departmentCount}
                    size={'small'}
                    color={'secondary'}
                  />
                </TableCell>
                <TableCell>
                  {format(new Date(org.createdAt), 'MMM d, yyyy')}
                </TableCell>
                <TableCell align={'right'}>
                  <Box sx={{ '& > button': { ml: 1 } }}>
                    <Tooltip title={'Edit Organization'}>
                      <span>
                        <IconButton
                          size={'small'}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditOrganization(org);
                          }}
                          disabled={!!org.deletedAt}
                        >
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={'Manage Users'}>
                      <span>
                        <IconButton
                          size={'small'}
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageUsers(org);
                          }}
                          disabled={!!org.deletedAt}
                        >
                          <Person />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={'Settings'}>
                      <span>
                        <IconButton
                          size={'small'}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSettings(org);
                          }}
                          disabled={!!org.deletedAt}
                        >
                          <Settings />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={'Delete Organization'}>
                      <IconButton
                        size={'small'}
                        color={'error'}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteOrganization(org);
                        }}
                        style={{
                          display: org.deletedAt ? 'none' : 'inline-flex',
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={'Restore Organization'}>
                      <IconButton
                        size={'small'}
                        color={'success'}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestoreOrganization(org);
                        }}
                        style={{
                          display: org.deletedAt ? 'inline-flex' : 'none',
                        }}
                      >
                        <Restore />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
